/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button, Col, Modal, Row,
} from 'react-bootstrap';
import { FiDownload } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import validateForm from 'utils/Validator';
import useSubmit from 'hooks/useSubmit';
import { yupChangePassword } from 'utils/yupFields';
import { Invitation } from 'types/Invitation';
import moment from 'moment';
import { getNdaStatusName } from 'utils/utils';
import AdminInvitationsServices from 'services/api/AdminInvitations';

type EditInvitationModalProps = {
  dataInvitation: Invitation | null,
  show: boolean,
  onClose: () => void,
  downloadNda: () => void,
  downloadDocument: (
    invitacionUid: string,
    documentoUid: string,
    nombreArchivo: string,
  ) => void,
  reloadData: () => void,
};

function EditInvitationModal( {
  show, dataInvitation, onClose, reloadData,
  downloadNda, downloadDocument,
}: EditInvitationModalProps ) {
  const { t } = useTranslation();
  const [formSubmitted, setFormSubmitted] = useState( false );
  const validationSchema = yup.object( yupChangePassword( t ) );
  const validate = validateForm( validationSchema );

  const submitForm = useSubmit( {
    promise: ( formData ) => AdminInvitationsServices.updateAdminInvitationsDocuments( formData ),
    callback: ( ) => {
      setFormSubmitted( true );
      reloadData();
    },
    showAlert: false,
  } );

  useEffect( () => {
    if ( !show ) setFormSubmitted( false );
  }, [show] );

  return (
    <Modal
      show={show}
      className="modal-carousel"
      centered
      size="xl"
      onHide={onClose}
    >
      <Modal.Header className="pb-0" closeButton />
      <Modal.Body className="pt-0">
        <Row>
          <Col md={10} className="m-auto">
            <h3 className="text-center mb-4">{t( 'admin.common.editInvitation' )}</h3>
            <div className="pt-4">
              <h4 className="bg-light p-2 rounded">General:</h4>
              <Row className="mt-3 px-3">
                <Col sm={3}>
                  Tipo:
                </Col>
                <Col>
                  {dataInvitation?.type}
                </Col>
              </Row>
              <Row className="mt-3 px-3">
                <Col sm={3}>
                  UUID:
                </Col>
                <Col sm={5}>
                  <span
                    onClick={() => {
                      if ( dataInvitation?.type !== 'OPORTUNIDAD' ) {
                        const url = `${window.location.origin}/link/informacion-confidencial/${dataInvitation?.invitacionUid}`;
                        window.open( url, '_blank' );
                      }
                    }}
                    style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}
                  >
                    {dataInvitation?.invitacionUid?.toUpperCase()}
                  </span>
                </Col>
              </Row>
              <Row className="mt-3 px-3">
                <Col sm={3}>
                  {dataInvitation?.type !== 'OPORTUNIDAD' ? 'Email' : 'Oportunidad'}
                  :
                </Col>
                <Col>
                  {dataInvitation?.type !== 'OPORTUNIDAD' ? dataInvitation?.email : dataInvitation?.codigoOportunidad}
                </Col>
              </Row>
              {dataInvitation?.type !== 'OPORTUNIDAD' && (
                <Row className="mt-4 px-3">
                  <Col sm={3}>
                    Estado Invitación:
                  </Col>
                  <Col sm={4}>
                    <Badge bg={dataInvitation?.estadoInvitacion === 'BLOQUEADA'
                      ? 'danger' : 'primary'}
                    >
                      {dataInvitation?.estadoInvitacion}
                    </Badge>
                  </Col>
                </Row>
              )}
              {dataInvitation?.type !== 'OPORTUNIDAD' && (
              <>
                <Row className="mt-4 px-3">
                  <Col sm={3}>
                    Estado nda:
                  </Col>
                  <Col>
                    <Row>
                      <Col>
                        <Badge>
                          {getNdaStatusName( dataInvitation?.ndaEstado || '', !!dataInvitation?.ndaEnvelopeId )}
                        </Badge>
                        {dataInvitation?.ndaEstado === 'COMPLETADO' && !!dataInvitation?.ndaEnvelopeId && (
                        <Button variant="link" size="sm" className="text--semibold p-0 mb-1 ms-3" onClick={() => downloadNda( )}>
                          (DESCARGAR NDA)
                        </Button>
                        )}
                      </Col>
                      <Col />
                    </Row>
                  </Col>
                </Row>
                <Row className="mt-4 px-3">
                  <Col sm={3}>
                    Último acceso:
                  </Col>
                  <Col>
                    {moment( dataInvitation?.lastAccessAt ).format( 'DD-MM-YYYY HH:mm:ss' )}
                  </Col>
                </Row>
                <Row className="mt-4 px-3">
                  <Col sm={3}>
                    Frecha de creación:
                  </Col>
                  <Col sm={4}>
                    {moment( dataInvitation?.createdAt ).format( 'DD-MM-YYYY HH:mm:ss' )}
                  </Col>
                </Row>
              </>
              )}
              {dataInvitation?.carpetasPaths && ( ( () => {
                let carpetasArray: string[] = [];
                try {
                  const parsed = typeof dataInvitation!.carpetasPaths === 'string'
                    ? JSON.parse( dataInvitation!.carpetasPaths as string )
                    : dataInvitation!.carpetasPaths;
                  carpetasArray = Array.isArray( parsed ) ? parsed : [];
                } catch {
                  carpetasArray = [];
                }
                return carpetasArray.length > 0 ? (
                  <>
                    <h4 className="mt-5 bg-light p-2 rounded">Carpetas compartidas</h4>
                    <ul className="mt-3 px-3 list-unstyled mb-0">
                      {carpetasArray.map( ( path, idx ) => (
                        <li key={path} className="text-break small py-1">
                          <span className="text-muted me-2">
                            {idx + 1}
                            .
                          </span>
                          {path.includes( 'VDR/' )
                            ? path.split( 'VDR/' )[1]
                            : path}
                        </li>
                      ) )}
                    </ul>
                  </>
                ) : null;
              } )() )}
              <h4 className="mt-5 bg-light p-2 rounded">Documentos</h4>
              <Row className="mt-3 px-3">
                <Col className="mb-1" xs={7}>
                  <span className="text-bold">
                    Nombre
                  </span>
                </Col>
                <Col className="mb-1" xs={2}>
                  <span className="text-bold">
                    Estado
                  </span>
                </Col>
                {dataInvitation?.type !== 'OPORTUNIDAD' && (
                  <>
                    <Col className="mb-1" xs={2}>
                      <span className="text-bold">
                        Descargas
                      </span>
                    </Col>
                    <Col className="mb-1" xs={1} />
                  </>
                )}

              </Row>
              {dataInvitation?.documentos?.map( ( item ) => (
                <Row className="mt-3 px-3 align-items-center" key={item.id}>
                  <Col className="mb-1" xs={7}>
                    <div>
                      <span className="text-bold d-block">{item.nombre}</span>
                      {item.path && item.path.includes( 'VDR/' ) && (
                        <span className="small text-muted d-block mt-0">
                          /
                          {item.path.split( 'VDR/' )[1]}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col className="mb-1" xs={2}>
                    <span className="text-bold">
                      {item.estado}
                    </span>
                  </Col>
                  {dataInvitation?.type !== 'OPORTUNIDAD' && (
                  <>
                    <Col className="mb-1" xs={2}>
                      <span className="text-bold">
                        {item?.cantidadDescargas}
                      </span>
                    </Col>
                    <Col className="mb-1" xs={1}>
                      <Button
                        className="p-0"
                        variant="link"
                        title="Descargar documento"
                        onClick={() => {
                          if ( dataInvitation?.invitacionUid && item.documentoUid ) {
                            downloadDocument(
                              dataInvitation.invitacionUid,
                              item.documentoUid,
                              item.nombre,
                            );
                          }
                        }}
                      >
                        <FiDownload />
                      </Button>
                    </Col>
                  </>
                  )}
                </Row>
              ) )}

            </div>
            {/* <Row className="mt-5">
              <Col className="mt-5">
                <Button
                  variant="primary"
                  type="button"
                  className="btn--full-width"
                  disabled
                >
                  {t( 'admin.common.closeInvitation' )}
                </Button>
              </Col>
            </Row> */}

            <Row>
              <Col className="mt-4">
                <Button
                  variant="primary"
                  type="button"
                  className="btn--full-width"
                  onClick={onClose}
                >
                  {t( 'close' )}
                </Button>
              </Col>
            </Row>

          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default EditInvitationModal;
